import React, { useState, useEffect } from 'react';
import { submitChildWeddingForm ,getAgentIdByToken} from '../services/api'; // Assuming you have an API service for wedding data
import '../css/ChildWeddingForm.css'; // You'll need to create this CSS file
import ChildWeddingFormTranslations from './ChildWeddingFormTranslations'; // Import wedding translations
import Modal from '../components/Modal';


const ChildWeddingForm = () => {
  const currentLanguage = 'en'; // Can be made dynamic
  const translations = ChildWeddingFormTranslations[currentLanguage];

  // Form states
  const [numChildren, setNumChildren] = useState('');
  const [children, setChildren] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [showChildrenDetails, setShowChildrenDetails] = useState(false);

  // Personal details states
  const [formTimestamp, setFormTimestamp] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [cityOfResidence, setCityOfResidence] = useState('');
  const [otherCity, setOtherCity] = useState('');
  const [gender, setGender] = useState(''); // Customer's gender
  const [dob, setDob] = useState(''); // Customer's DOB
  const [customerAge, setCustomerAge] = useState(''); // Customer's calculated age
  const [showModal, setShowModal] = useState(false);
  const [agentId, setAgentId] = useState(null);
  const [loadingAgent, setLoadingAgent] = useState(true);
  const [agentError, setAgentError] = useState('');

  // Error states
  const [errors, setErrors] = useState({}); // For personal details
  const [childrenErrors, setChildrenErrors] = useState([]); // For children details

  // Fetch agent ID from backend using token in URL
      useEffect(() => {
          const fetchAgentId = async () => {
              try {
                  // Extract token from URL path
                  const pathParts = window.location.pathname.split('/');
                  const token = pathParts[pathParts.length - 1];
                  
                  if (token) {
                      const response = await getAgentIdByToken(token);
                      console.log("==response health ==",response.data)
  
                      setAgentId(response.data);
                  } else {
                      // Fallback to localStorage if token not found
                      const storedAgentId = localStorage.getItem("agentId");
                      if (storedAgentId) {
                          setAgentId(parseInt(storedAgentId, 10));
                      } else {
                          setAgentError("Agent information could not be loaded");
                      }
                  }
              } catch (error) {
                  console.error('Error fetching agent ID:', error);
                  setAgentError("Failed to load agent information");
                  
                  // Fallback to localStorage
                  const storedAgentId = localStorage.getItem("agentId");
                  if (storedAgentId) {
                      setAgentId(parseInt(storedAgentId, 10));
                  }
              } finally {
                  setLoadingAgent(false);
              }
          };
  
          fetchAgentId();
      }, []);

  useEffect(() => {
    // Set initial timestamp
    setFormTimestamp(new Date().toISOString().slice(0, 19).replace('T', ' '));
  }, []);

  // Calculate customer's age when DOB changes
  useEffect(() => {
    if (dob) {
      const dobDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - dobDate.getFullYear();
      const m = today.getMonth() - dobDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
        age--;
      }
      setCustomerAge(age); // Store calculated age in state
    } else {
      setCustomerAge(''); // Reset age if DOB is cleared
    }
  }, [dob]);


  const generateChildForms = () => {
    const num = parseInt(numChildren);
    if (isNaN(num) || num < 0 || num > 5) { // Max 5 children for wedding planning
      console.warn(translations.errors.invalidNumberOfChildren || "Please enter a number between 1 and 5.");
      setChildren([]);
      setChildrenErrors([]);
      return;
    }
    setChildren(Array(num).fill().map(() => ({
      name: '',
      gender: translations.data.childGenderOptions[0]?.value || 'Male',
      age: '', // Child's current age
      goldCost: '', // Current Gold Cost (INR)
      weddingCost: '', // Current Wedding Cost (INR)
    })));
    setChildrenErrors(Array(num).fill({}));
    setShowChildrenDetails(true);
  };

  const handleChildChange = (index, field, value) => {
    const updatedChildren = [...children];
    updatedChildren[index][field] = value;
    setChildren(updatedChildren);

    const updatedChildErrors = [...childrenErrors];
    updatedChildErrors[index] = { ...updatedChildErrors[index], [field]: '' };
    setChildrenErrors(updatedChildErrors);
  };

  const roundAndFormat = (value) => {
    if (isNaN(value) || value === null || value <= 0) return '';
    const roundedValue = Math.round(value / 10000) * 10000;

    const croresLabel = translations.labels.crores || 'Crores';
    const lakhsLabel = translations.labels.lakhs || 'Lakhs';

    if (roundedValue >= 10000000) {
      return `${(roundedValue / 10000000).toFixed(2)} ${croresLabel}`;
    } else if (roundedValue >= 100000) {
      return `${(roundedValue / 100000).toFixed(2)} ${lakhsLabel}`;
    } else {
      return `${roundedValue.toLocaleString('en-IN')}`; // For values less than 1 Lakh
    }
  };

  const validatePersonalDetails = () => {
    let newErrors = {};

    if (!name) newErrors.name = translations.errors.nameRequired;
    if (!email) {
      newErrors.email = translations.errors.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = translations.errors.invalidEmail;
    }
    if (!mobile) {
      newErrors.mobile = translations.errors.mobileRequired;
    } else if (!/^\d{10}$/.test(mobile) || /(.)\1{3}/.test(mobile)) {
      newErrors.mobile = translations.errors.invalidMobile;
    }
    if (!cityOfResidence) {
      newErrors.cityOfResidence = translations.errors.cityRequired;
    } else if (cityOfResidence === 'Others' && !otherCity) {
      newErrors.otherCity = translations.errors.requiredOtherCity;
    }
    if (!dob) {
      newErrors.dob = translations.errors.dobRequired;
    } else {
      const birthDate = new Date(dob);
      const today = new Date();
      birthDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      if (isNaN(birthDate.getTime()) || birthDate > today) {
        newErrors.dob = translations.errors.dateInvalid;
      }
    }
    if (!gender) newErrors.gender = translations.errors.genderRequired; // Customer's gender

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const validateChildrenDetails = () => {
    let newChildrenErrors = children.map(() => ({}));
    let allChildrenValid = true;

    if (children.length === 0 && parseInt(numChildren) > 0) {
      console.warn(translations.errors.numberOfChildrenRequired || "Please specify the number of children and generate their forms first.");
      return false;
    }
    if (children.length === 0 && parseInt(numChildren) === 0) {
      return true; // Valid scenario if no children are planned
    }

    children.forEach((child, index) => {
      let childErrors = {};

      if (!child.name) childErrors.name = translations.errors.childNameRequired;

      const age = parseInt(child.age);
      if (isNaN(age) || age < 1 || age > 26) { // Wedding age up to 26
        childErrors.age = translations.errors.invalidChildAge || 'Age must be between 1 and 26.';
      }

      if (isNaN(parseFloat(child.goldCost)) || parseFloat(child.goldCost) < 0) {
        childErrors.goldCost = translations.errors.invalidGoldCost || 'Gold Cost must be a positive number.';
      }
      if (isNaN(parseFloat(child.weddingCost)) || parseFloat(child.weddingCost) < 0) {
        childErrors.weddingCost = translations.errors.invalidWeddingCost || 'Wedding Cost must be a positive number.';
      }

      newChildrenErrors[index] = childErrors;
      if (Object.keys(childErrors).length > 0) {
        allChildrenValid = false;
      }
    });

    setChildrenErrors(newChildrenErrors);
    return allChildrenValid;
  };

  const handleSubmitForm = async () => {

    if (!validatePersonalDetails() || !validateChildrenDetails()) {
      console.warn(translations.errors.validationFailed || "Please correct the form errors.");
      return;
    }
  
    let total = 0;
   // const agentId = localStorage.getItem("agentId");
  
    // It's better to calculate the total while mapping for children_details
    // to avoid recalculating inflation twice or having stale values.
    const childrenDetailsForSubmission = children.map(child => {
      const age = parseInt(child.age);
      const yearsToWedding = 27 - age;
      const goldInflationRate = 0.125; // 12.5%
      const weddingInflationRate = 0.06; // 6%
  
      const futureGoldCost = parseFloat(child.goldCost) * Math.pow(1 + goldInflationRate, yearsToWedding);
      const futureWeddingCost = parseFloat(child.weddingCost) * Math.pow(1 + weddingInflationRate, yearsToWedding);
  
      // --- You can keep these console logs here to see the calculation for each child ---
      console.log(`Child ${child.name || 'Unnamed'}:`);
      console.log(`  Current Gold Cost: ${child.goldCost}`);
      console.log(`  Years to Wedding: ${yearsToWedding}`);
      console.log(`  Calculated Future Gold Cost: ${futureGoldCost}`);
      console.log(`  Current Wedding Cost: ${child.weddingCost}`);
      console.log(`  Calculated Future Wedding Cost: ${futureWeddingCost}`);
      // ---------------------------------------------------------------------------------
  
      // Add this child's total to the overall total
      total += (futureGoldCost + futureWeddingCost);
  
      return {
        'child_name': child.name,
        'gender': child.gender,
        'age': parseInt(child.age),
        'estimated_gold_cost': parseFloat(child.goldCost),
        'estimated_wedding_cost': parseFloat(child.weddingCost),
        'future_estimated_gold_cost': futureGoldCost, // This will be the specific value for this child
        'future_estimated_wedding_cost': futureWeddingCost, // This will be the specific value for this child
      };
    });
  
  
    setTotalCost(total); // Update total cost state
  
    // Prepare formData for submission to the database
    const formData = {
      'form_timestamp': formTimestamp,
      'name': name, // Customer's name
      'email': email,
      'mobile': mobile,
      'city_of_residence': cityOfResidence === 'Others' ? otherCity : cityOfResidence,
      'dob': dob,
      'customer_age': customerAge, // Customer's calculated age
      'customer_gender': gender, // Customer's gender
      'num_children': children.length,
      'agent_id': agentId, // Ensure agentId is captured
      'total_wedding_expenses': total, // Renamed for wedding
      'children_details': childrenDetailsForSubmission, // Use the array created by map
    };
  
    console.log("FormData being sent to API:", formData);

    try {
      const response = await submitChildWeddingForm(formData); // Use new API call
      console.log("===response==", response.data);
      console.log(translations.labels.submission_success || 'Form submitted successfully!');
      setShowModal(true); // Show the modal
      resetForm();
    } catch (error) {
      console.error('Error submitting form:', error);
      console.error(translations.errors.submitError || 'Failed to submit data. Please try again.');
    }
  };

  const resetForm = () => {
    setNumChildren('');
    setChildren([]);
    setTotalCost(0);
    // No exchange rate to fetch for wedding form
    setName('');
    setEmail('');
    setMobile('');
    setCityOfResidence('');
    setOtherCity('');
    setGender('');
    setDob('');
    setCustomerAge('');
    setErrors({});
    setChildrenErrors([]);
    setShowChildrenDetails(false);

   
    setFormTimestamp(new Date().toISOString().slice(0, 19).replace('T', ' '));
  };

  return (
    <div className="child-wedding-form-container">
      <div className="child-wedding-form">
        <h1>{translations.labels.title}</h1>
        <p style={{ color: 'rgb(112, 16, 99)' }}>
          {translations.labels.description}
        </p>
            <div className="personal-details-section">
              <label htmlFor="formTimestamp">{translations.labels.form_timestamp}:</label>
              <input
                type="text"
                id="formTimestamp"
                value={formTimestamp}
                readOnly
                className="form-input"
              />
              <label htmlFor="name">{translations.labels.name}:</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={translations.placeholders.name}
                className="form-input"
              />
              {errors.name && <p className="error-message">{errors.name}</p>}

              <label htmlFor="email">{translations.labels.email}:</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={translations.placeholders.email}
                className="form-input"
              />
              {errors.email && <p className="error-message">{errors.email}</p>}

              <label htmlFor="mobile">{translations.labels.mobile}:</label>
              <input
                type="tel"
                id="mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder={translations.placeholders.mobile}
                maxLength="10"
                className="form-input"
              />
              {errors.mobile && <p className="error-message">{errors.mobile}</p>}

              <label htmlFor="cityOfResidence">{translations.labels.city_of_residence}:</label>
              <select
                id="cityOfResidence"
                value={cityOfResidence}
                onChange={(e) => {
                  setCityOfResidence(e.target.value);
                  if (e.target.value !== 'Others') {
                    setOtherCity('');
                  }
                }}
                className="form-select"
              >
                <option value="">{translations.placeholders.city_of_residence}</option>
                {translations.data.cityOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {errors.cityOfResidence && <p className="error-message">{errors.cityOfResidence}</p>}

              {cityOfResidence === 'Others' && (
                <div>
                  <label htmlFor="otherCity">{translations.labels.otherCity}:</label>
                  <input
                    type="text"
                    id="otherCity"
                    value={otherCity}
                    onChange={(e) => setOtherCity(e.target.value)}
                    placeholder={translations.placeholders.otherCity}
                    className="form-input"
                  />
                  {errors.otherCity && <p className="error-message">{errors.otherCity}</p>}
                </div>
              )}

              <label htmlFor="gender">{translations.labels.gender}:</label>
              <select
                id="gender"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="form-select"
              >
                <option value="">{translations.placeholders.gender}</option>
                {translations.data.customerGenderOptions.map(option => ( // Use customer gender options
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {errors.gender && <div className="error-message">{errors.gender}</div>}

              <label htmlFor="dob">{translations.labels.dob}:</label>
              <input
                type="date"
                id="dob"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                max={new Date().toISOString().split('T')[0]} // Max date is today
                placeholder={translations.placeholders.dob}
                className="form-input"
              />
              {errors.dob && <div className="error-message">{errors.dob}</div>}



                    
            </div>
         

            <div className="children-details-section">
              <h2>{translations.labels.children_details || "Children's Details"}</h2>
              <label htmlFor="numChildren">{translations.labels.number_of_children}:</label>
              <select
                id="numChildren"
                value={numChildren}
                onChange={(e) => setNumChildren(e.target.value)}
                className="form-select"
              >
                <option value="">{translations.placeholders.number_of_children}</option>
                {translations.data.numberOfChildrenOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>

              <input
                type="button"
                value={translations.labels.generate_child_forms || "Generate Child Forms"}
                onClick={generateChildForms}
                className="submit-button"
              />
             

              <div className="child-form">
                {children.map((child, index) => (
                  <div key={index} className="child-section">
                    <h3>{translations.labels.child_number || "Child"} {index + 1}</h3>

                    <label>{translations.labels.child_name}:</label>
                    <input
                      type="text"
                      value={child.name}
                      onChange={(e) => handleChildChange(index, 'name', e.target.value)}
                      placeholder={translations.placeholders.child_name}
                      className="form-input"
                    />
                    {childrenErrors[index]?.name && <div className="error">{childrenErrors[index].name}</div>}

                    <label>{translations.labels.gender}:</label>
                    <select
                      value={child.gender}
                      onChange={(e) => handleChildChange(index, 'gender', e.target.value)}
                      className="form-select"
                    >
                      {translations.data.childGenderOptions.map(option => ( // Use child gender options
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    {childrenErrors[index]?.gender && <div className="error">{childrenErrors[index].gender}</div>}

                    <label>{translations.labels.age} (1-26):</label>
                    <input
                      type="text"
                      min="1"
                      max="26"
                      value={child.age}
                      onChange={(e) => handleChildChange(index, 'age', e.target.value)}
                      className="form-input"
                      placeholder={translations.placeholders.age}
                       pattern="[0-9]*" 
                      inputMode="numeric"
                    />
                    {childrenErrors[index]?.age && <div className="error">{childrenErrors[index].age}</div>}

                    <label>{translations.labels.currentGoldCostInr}:</label>
                    <input
                      type="text"
                      value={child.goldCost}
                      onChange={(e) => handleChildChange(index, 'goldCost', e.target.value)}
                      className="form-input"
                      placeholder={translations.placeholders.currentGoldCostInr}
                       pattern="[0-9]*" 
                      inputMode="numeric"
                    />
                    {childrenErrors[index]?.goldCost && <div className="error">{childrenErrors[index].goldCost}</div>}

                    <label>{translations.labels.currentWeddingCostInr}:</label>
                    <input
                      type="text"
                      value={child.weddingCost}
                      onChange={(e) => handleChildChange(index, 'weddingCost', e.target.value)}
                      className="form-input"
                      placeholder={translations.placeholders.currentWeddingCostInr}
                       pattern="[0-9]*" 
                      inputMode="numeric"
                    />
                    {childrenErrors[index]?.weddingCost && <div className="error">{childrenErrors[index].weddingCost}</div>}
                  </div>
                ))}

                {children.length > 0 && ( // Only show calculate button if children forms are generated
                  <input
                    type="button"
                    value={translations.labels.calculate || "Submit"}
                    onClick={handleSubmitForm} // This will trigger calculation and submission
                    className="submit-button"
                  />
                )}
              </div>
            </div>
          

          <div className="assumptions">
            <strong style={{ color: 'rgb(112, 16, 99)' }} >Assumptions:</strong>

            <ul>
              <li>{translations.labels.goldInflation}: 12.5%</li>
              <li>{translations.labels.weddingInflation}: 6%</li>
              <li>{translations.labels.weddingAge}: 27 {translations.labels.years}</li>
            </ul>
          </div>
      {/* Success Modal */}
      <Modal show={showModal} onClose={() => {
          setShowModal(false);
          resetForm();
        }}>
          <h2>🎉 Form Submitted Successfully!</h2>
          <p>Thank you for your response.</p>
         
        </Modal>
       
      </div>
    </div>
  );
};

export default ChildWeddingForm;
